import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlags, CustomerAccountItem } from '@inyova/models';
import { ShepherdService } from 'angular-shepherd';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as fromAppRoot from '@app/app.reducers';

import { INVESTMENT_LIMITS } from '@app/app.constants';
import { getCookie, removeCookie } from '@shared/utils';

@Component({
  selector: 'app-product-switch',
  templateUrl: './product-switch.component.html',
  styleUrls: ['./product-switch.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ProductSwitchComponent implements OnDestroy {
  private readonly onDestroy$ = new Subject<void>();
  userAccounts: CustomerAccountItem[];
  userAccountTypes: string[];
  dispatched = false;
  currency: string;
  language: string;
  MINIMUM_INVESTMENT: number;
  MINIMUM_INVESTMENT_3B: number;
  MINIMUM_INVESTMENT_GROW: number;
  appLocation: string;
  isLoading = false;
  isPro = false;
  isEve2EnabledFor3B = false;

  constructor(
    private store: Store,
    private router: Router,
    private shepherdService: ShepherdService
  ) {
    if (this.shepherdService.isActive) {
      this.shepherdService.complete();
    }
    const product = getCookie('product');
    if (product) {
      removeCookie('product');
      this.createNewAccount(product);
    }

    this.store.pipe(select(fromAppRoot.selectCustomerAndCurrentAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      if (!value || !value.customer || !value.customer.email) {
        return;
      }

      // don't allow the user to see this page
      if (value.customer.app_location !== 'ch' || value.customer.accounts.length) {
        void this.router.navigate(['/']);
      }

      this.language = value.customer.language;
      this.appLocation = value.customer.app_location;
      this.isPro = value.customer.inyova_pro;
      this.currency = value.customer.app_location === 'ch' ? 'CHF' : 'EUR';
      this.isEve2EnabledFor3B = value.customer.enabled_features.includes(FeatureFlags.EVE2_3B);
      this.userAccounts = value.customer.temporary_accounts.filter((account) => account);
      this.userAccountTypes = this.userAccounts.map((account) => account.kind as string);
      this.MINIMUM_INVESTMENT = value.currentAccountDetail.minimum_investment;
      this.MINIMUM_INVESTMENT_3B = this.MINIMUM_INVESTMENT_GROW = INVESTMENT_LIMITS.initial[value.customer.app_location];
      if (this.dispatched) {
        void this.router.navigate(['/strategy']);
        this.dispatched = false;
      }
    });

    this.store.pipe(select(fromAppRoot.selectCreateNewAccountIsLoading), takeUntil(this.onDestroy$)).subscribe((isLoading) => {
      this.isLoading = isLoading;
    });
  }

  createNewAccount(kind: string): void {
    if (this.isLoading) {
      return;
    }

    const payload = { kind, appLocation: this.appLocation };
    // temp hard redirect until account creation is modified
    if (kind === 'grow') {
      void this.router.navigate(['/grow/intro']);
    } else {
      this.store.dispatch(AppRootActions.toggleNewAccountIsLoading({ isLoading: true }));
      this.store.dispatch(AppRootActions.createNewAccount(payload));
    }
  }

  stopProp(event: Event): void {
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
